import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { services } from '../data/mock';

const Navbar = ({ onOpenPopup }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isLandingPage = location.pathname === '/lp';

  if (isLandingPage) {
    return null;
  }


  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/assets/img/logo.png" alt="Trademark Prime Unit" className="h-[3.5rem] w-auto" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-primary font-medium transition-colors">
              Home
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-primary font-medium transition-colors">
              About Us
            </Link>
            
            {/* Services Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => setShowMegaMenu(true)}
              onMouseLeave={() => setShowMegaMenu(false)}
            >
              <button className="text-gray-700 hover:text-primary font-medium transition-colors flex items-center">
                Services
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${showMegaMenu ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`absolute left-1/2 -translate-x-1/2 top-full w-96 pt-2 transition-all duration-200 ease-out ${
                  showMegaMenu
                    ? 'opacity-100 translate-y-0 visible'
                    : 'opacity-0 -translate-y-2 invisible pointer-events-none'
                }`}
              >
                <div className="bg-white rounded-lg shadow-2xl border border-gray-100 p-6">
                  <div className="grid gap-3">
                    {services.map((service) => (
                      <Link
                        key={service.id}
                        to={`/services/${service.slug}`}
                        className="p-3 rounded-lg hover:bg-secondary-100 transition-colors group"
                      >
                        <h3 className="font-semibold text-gray-800 group-hover:text-primary transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Link to="/contact" className="text-gray-700 hover:text-primary font-medium transition-colors">
              Contact Us
            </Link>
          </div>

          {/* Right Side Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              onClick={onOpenPopup}
              className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Register My Trademark
            </Button>
            <Button
              
              variant="outline"
              className="js-chat border-2 border-primary-600 text-primary hover:bg-secondary-100 hover:text-primary-800 px-6 py-2 rounded-lg font-semibold transition-all duration-300"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Chat Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-gray-700 hover:text-primary-400 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-gray-700 hover:text-primary-400 font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-gray-700 hover:text-primary-400 font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                About Us
              </Link>
              
              <div className="text-gray-700 font-medium">Services</div>
              <div className="pl-4 flex flex-col space-y-2">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.slug}`}
                    className="text-gray-600 hover:text-primary-400 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {service.title}
                  </Link>
                ))}
              </div>

              <Link
                to="/contact"
                className="text-gray-700 hover:text-primary-400 font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </Link>
              
              <div className="pt-4 space-y-2">
                <Button
                  onClick={() => {
                    onOpenPopup();
                    setIsOpen(false);
                  }}
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                >
                  Register My Trademark
                </Button>
                <Button
                
                  variant="outline"
                  className="js-chat w-full border-2 border-primary-600 text-primary hover:bg-secondary-100"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
